import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSchemaIntegrationData } from "../hooks/useSchemaIntegrationData";
import type { InformationCardComponentProps } from "../types";
import { getIcon } from "../utils/iconMap";

import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import { Card, CardContent } from "@/components/ui/v2/card";
import useToast from "@/lib/hooks/useToast";

/**
 * InformationCardComponent - renders a card with fields (icon, key, value).
 * Each field uses `dataKey` to bind its value to the corresponding data field.
 * Generic component that uses hook factory pattern to fetch data.
 */
function InformationCardComponent({
  fields = [],
  data,
  renderWithContext,
  executeAction,
  integrationName,
  moduleName,
  clientId,
  tenantId,
}: InformationCardComponentProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  
  // Use generic hook to fetch integration-specific data (resolves to integration-specific hook)
  const moduleIntegrationDataResult = useSchemaIntegrationData(
    integrationName,
    moduleName ?? "",
    clientId,
    tenantId
  );
  
  // Merge context data with module-specific data
  const componentData = useMemo(() => {
    const merged = {
      ...data,
      ...moduleIntegrationDataResult.data,
    };
    return merged;
  }, [data, moduleIntegrationDataResult.data]);

  if (fields.length === 0) {
    return null;
  }

  // Check if this is a loading state by checking if ANY field's value is "Loading"
  // This is generic - works for any card that has any field set to "Loading"
  const hasLoadingField = fields.some(field => {
    if (!field.dataKey) return false;
    const value = componentData?.[field.dataKey];
    return value === "Loading";
  });
  
  const hasFeedStatusField = fields.some((f) => f.dataKey === "feedStatus");
  const hasStatusField = fields.some((f) => f.dataKey === "status");
  const feedStatusIsLoading = hasFeedStatusField && componentData?.feedStatus === "Loading";
  const statusIsLoading = hasStatusField && componentData?.status === "Loading";
  const isLoading = hasLoadingField || feedStatusIsLoading || statusIsLoading;
  
  // Check if failed - look for any status field that might indicate failure
  const isFailed = componentData?.feedStatus === "Failed" || componentData?.status === "Failed";
  
  // If loading, show loading spinner with configurable messages from data
  if (isLoading) {
    const loadingTitleKey = (componentData?.loadingTitleKey as string) || "common.loading";
    const loadingDescriptionKey = (componentData?.loadingDescriptionKey as string) || 
                                  (componentData?.loadingMessageKey as string) || 
                                  "common.loadingDescription";
    
    return (
      <Card className="h-full min-h-[320px] flex flex-col">
        <CardContent className="p-6 flex-1 flex flex-col">
          <div className="flex flex-col items-center justify-center flex-1 gap-4">
            <Icons.ProgressCircleIcon fontSize={24} className="text-primary animate-spin" />
            <div className="text-center space-y-3 w-full">
              <p className="text-sm font-medium">
                {t(loadingTitleKey)}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(loadingDescriptionKey)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If failed, show error message with action button (if action is configured in data)
  if (isFailed) {
    // Get action configuration from data - prefer failureRetryAction to avoid showing top-right button
    // fallback to retryAction for backwards compatibility
    const actionKey = (componentData?.failureRetryAction ?? componentData?.retryAction) as string | undefined;
    const failureTitleKey = (componentData?.failureTitleKey as string) || "common.error";
    const failureDescriptionKey = (componentData?.failureDescriptionKey as string) || "common.error";
    const actionLabelKey = (componentData?.actionLabelKey as string) || "common.retry";
    const successMessageKey = (componentData?.successMessageKey as string) || "common.success";
    const errorMessageKey = (componentData?.errorMessageKey as string) || "common.error";

    const handleAction = async () => {
      if (!actionKey) {
        showToast(t("common.actionNotConfigured"), "error");
        return;
      }

      if (!clientId && componentData?.requiresClientId) {
        showToast(t("common.clientIdRequired"), "error");
        return;
      }

      // Use executeAction to trigger action generically
      await executeAction(actionKey, {
        successMessage: successMessageKey,
        errorMessage: errorMessageKey,
      });
      
      // Refetch integration data after successful action
      moduleIntegrationDataResult.refetch();
    };

    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="rounded-lg p-2 bg-destructive/10">
              <svg
                className="h-6 w-6 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <div className="text-center space-y-2">
              <p className="text-sm font-medium text-destructive">
                {t(failureTitleKey)}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(failureDescriptionKey)}
              </p>
            </div>
            {actionKey && clientId && (
              <Button
                onClick={handleAction}
                variant="default"
                size="sm"
              >
                <Icons.SyncIcon fontSize={16} />
                {t(actionLabelKey)}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const renderFields = (sectionFields: typeof fields) => (
    <div className="space-y-4 flex-1">
      {sectionFields.map((field, index) => {
        const IconComponent = getIcon(field.icon);

        // Use dataKey to extract the field's value from data
        const dataKey = field.dataKey;
        const rawValue = componentData != null && dataKey ? componentData[dataKey] : null;
        const isEmptyText =
              rawValue == null ||
              (typeof rawValue === "string" && rawValue.trim() === "");
        const valueComponent = field.value?.component;
        if (
          isEmptyText &&
              (valueComponent === "text" ||
                valueComponent === "link")
        ) {
          return null;
        }

        // Check if this field value indicates loading state
        const isFieldLoading = rawValue === "Loading";

        const variantKey =
              dataKey != null ? `${String(dataKey)}Variant` : undefined;
        const variantValue =
              variantKey != null && componentData != null
                ? componentData[variantKey]
                : undefined;
        const labelKey =
              dataKey != null ? `${String(dataKey)}Label` : undefined;
        const labelValue =
              labelKey != null && componentData != null
                ? componentData[labelKey]
                : undefined;
            
        // Build context data for the value component
        const fieldData =
              rawValue != null
                ? {
                  text: String(rawValue),
                  href: String(rawValue),
                  value: rawValue,
                  variant: variantValue,
                  label: labelValue != null ? String(labelValue) : undefined,
                }
                : variantValue != null || labelValue != null
                  ? { variant: variantValue, label: labelValue != null ? String(labelValue) : undefined }
                  : undefined;

        // Show loading spinner for any field with "Loading" value
        const loadingMessageKey = componentData?.loadingMessageKey as string | undefined;
        const valueNode = isFieldLoading
          ? (
            <div className="flex items-center gap-2">
              <Icons.ProgressCircleIcon fontSize={16} className="text-primary animate-spin" />
              {loadingMessageKey && (
                <span className="text-muted-foreground text-xs">
                  {t(loadingMessageKey)}
                </span>
              )}
            </div>
          )
          : field.value
            ? renderWithContext(field.value, { data: fieldData })
            : rawValue != null
              ? String(rawValue)
              : null;

        return (
          <div
            key={field.key + index}
            className="flex items-start gap-3 text-sm"
          >
            {IconComponent != null && (
              <IconComponent className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
            )}
            <div className="flex flex-1 flex-col gap-0.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">
                  {(() => {
                    // Check if this is the problematic status key that returns an object
                    // If so, use .label suffix instead
                    const translationKey = field.key === "integrations.werkzoeken.jobSync.feedRegistration.status"
                      ? "integrations.werkzoeken.jobSync.feedRegistration.status.label"
                      : field.key;

                    const translationResult = t(translationKey);
                    const isObject = typeof translationResult === "object" && translationResult !== null;
                    const isErrorMsg = typeof translationResult === "string" && translationResult.includes("returned an object instead of string");
                    
                    // Handle case where translation key returns an object (e.g., status: {label: "Status", mismatch: "Mismatch"})
                    if (isObject) {
                      // Try to extract a label property, or use the key as fallback
                      return (translationResult as { label?: string }).label ?? field.key;
                    }

                    // Handle case where i18next returns error message as string
                    if (isErrorMsg) {
                      // Try to get the .label version
                      const labelKey = field.key + ".label";
                      const labelResult = t(labelKey, { defaultValue: field.key });
                      return labelResult !== labelKey ? labelResult : field.key;
                    }

                    return translationResult;
                  })()}
                </span>
              </div>
              <span className="font-medium">
                {valueNode}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Card className="h-full min-h-[320px] flex flex-col">
      <CardContent className="pt-6 flex-1 flex flex-col">
        {renderFields(fields)}
      </CardContent>
    </Card>
  );
}

export { InformationCardComponent };
